import {useCallback, useRef} from 'react';
import {Modal} from 'antd';
import {useModalStore} from '@/store/useModalStore';
import {getOrigin} from '@/utils';
import './LoginModal.css'; // 全局弹窗hack样式

const LoginModal: React.FC = () => {
    const open = useModalStore((s) => s.loginModalOpen);
    const toggleLoginModal = useModalStore((s) => s.toggleLoginModal);
    const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const passportRenderTokenRef = useRef(0);
    const isPassportRenderingRef = useRef(false);
    const origFocusRef = useRef<typeof HTMLElement.prototype.focus | null>(null);

    const afterOpenChange = useCallback((visible: boolean) => {
        if (retryTimer.current !== null) clearTimeout(retryTimer.current);

        const closeBtn = document.querySelector<HTMLButtonElement>(
            '.login-modal-root .ant-modal-close',
        );

        if (!visible) {
            // 恢复 close 按钮的 focus 方法
            if (closeBtn && origFocusRef.current) {
                closeBtn.focus = origFocusRef.current;
                origFocusRef.current = null;
            }
            return;
        }

        // 中和 focus trap：覆盖 close 按钮的 focus 方法，防止 rc-dialog 的 syncFocus
        // 在检测到焦点离开 modal 时强制拉回 close 按钮，导致 passport 验证码 input 无法获焦
        if (closeBtn) {
            origFocusRef.current = closeBtn.focus;
            // eslint-disable-next-line func-names
            closeBtn.focus = function () {};
        }

        // Passport SDK 未加载时给出提示，避免报错
        if (!window.passport) {
            console.warn('[LoginModal] Passport SDK 未加载，登录功能暂不可用');
            return;
        }

        const hasPassportRendered = () =>
            Boolean(
                // 用稳定 class 判断是否已渲染
                document.querySelector('#passport-login .compose-left-container'),
            );

        const initPassport = () => {
            // 避免在 DOM 尚未生成时重复发起 render
            if (isPassportRenderingRef.current || hasPassportRendered()) return;

            isPassportRenderingRef.current = true;
            const renderToken = ++passportRenderTokenRef.current;

            window.passport?.use('login', {tangram: true, loginVersion: 'v5'}, (apiMagic) => {
                // 重试后旧回调可能晚返回，旧回调不应再 render，也不应释放当前初始化状态
                if (renderToken !== passportRenderTokenRef.current) return;

                if (hasPassportRendered()) {
                    isPassportRenderingRef.current = false;
                    return;
                }

                // eslint-disable-next-line new-cap
                const loginInstance = new apiMagic.passport.login({
                    product: 'adcp',
                    staticPage: `${getOrigin()}/v3Jump.html`,
                    u: window.location.href,
                    memberPass: true,
                    sms: 5,
                    userPwdLogin: 2,
                    qrcodeLogin: 3,
                    defaultCss: true,
                    loginMerge: true,
                    autosuggest: true,
                    qrcode: true,
                    hasPlaceholder: true,
                    authsiteLogin: ['qzone', 'tsina', 'weixin'],
                    hasRegUrl: true,
                    uniqueId: true,
                    hideLogo: 1,
                    isSyncBaiduInt: 1,
                    composeTemplate: 1,
                });

                loginInstance.render('passport-login');
                isPassportRenderingRef.current = false;

                loginInstance.on('loginError', () => {
                    console.error('Passport 登录失败');
                });
            });
        };

        initPassport();

        // 超过 2 秒未加载成功则重试
        retryTimer.current = setTimeout(() => {
            if (!hasPassportRendered()) {
                isPassportRenderingRef.current = false;
                initPassport();
            }
        }, 2000);
    }, []);

    const handleCancel = useCallback(() => {
        toggleLoginModal(false);
    }, [toggleLoginModal]);

    return (
        <Modal
            forceRender
            centered
            open={open}
            rootClassName="login-modal-root"
            afterOpenChange={afterOpenChange}
            footer={null}
            width={764}
            maskClosable={false}
            onCancel={handleCancel}
            styles={{
                mask: {
                    backdropFilter: 'blur(5px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
            }}
        >
            <div>
                <div id="passport-login" />
            </div>
        </Modal>
    );
};

export default LoginModal;
